import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { integrationService } from '../../services/integrationService';

const NotionForm = ({ formData, setFormData, isSubmitting, handleCreateIntegration }) => {
  const [authStatus, setAuthStatus] = useState('idle'); // idle, loading, success, error
  const [databases, setDatabases] = useState([]);
  const [isLoadingDatabases, setIsLoadingDatabases] = useState(false);

  // Generate redirect URI for OAuth callback
  const getRedirectUri = () => {
    return `${window.location.origin}/notion-callback`;
  };

  // Get Notion auth URL
  const getNotionAuthUrl = async () => {
    setAuthStatus('loading');
    try {
      const redirectUri = getRedirectUri();
      const result = await integrationService.getNotionAuthUrl(redirectUri);

      if (result.auth_url) {
        // Open the auth URL in a new window
        const authWindow = window.open(result.auth_url, '_blank', 'width=800,height=600');

        // Poll for auth completion
        const checkAuthInterval = setInterval(async () => {
          try {
            // Check if the window was closed
            if (authWindow.closed) {
              clearInterval(checkAuthInterval);

              // Check if we have a temporary code in localStorage (set by the callback page)
              const tempCode = localStorage.getItem('notion_temp_code');
              if (tempCode) {
                // Complete the auth flow
                const authResult = await integrationService.completeNotionAuth(tempCode, redirectUri);

                if (authResult.access_token) {
                  // Update form data with the access token
                  setFormData(prev => ({
                    ...prev,
                    access_token: authResult.access_token,
                    workspace_name: authResult.workspace_name || 'Notion Workspace',
                    workspace_id: authResult.workspace_id
                  }));

                  setAuthStatus('success');

                  // Fetch available databases
                  fetchNotionDatabases(authResult.access_token);

                  // Clear the temporary code
                  localStorage.removeItem('notion_temp_code');
                }
              } else {
                setAuthStatus('error');
              }
            }
          } catch (error) {
            console.error('Error checking auth status:', error);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error getting Notion auth URL:', error);
      setAuthStatus('error');
    }
  };

  // Fetch Notion databases
  const fetchNotionDatabases = async (accessToken) => {
    setIsLoadingDatabases(true);
    try {
      const token = accessToken || formData.access_token;
      if (!token) return;

      const result = await integrationService.getNotionDatabases(token);
      setDatabases(result.databases || []);
    } catch (error) {
      console.error('Error fetching Notion databases:', error);
    } finally {
      setIsLoadingDatabases(false);
    }
  };

  // Handle database selection
  const handleDatabaseChange = (e) => {
    const selectedDb = databases.find(db => db.id === e.target.value);

    setFormData(prev => ({
      ...prev,
      database_id: e.target.value,
      database_name: selectedDb?.title || 'Selected Database'
    }));
  };

  return (
    <form onSubmit={handleCreateIntegration} className="space-y-6">
      <div>
        <label className="block text-gray-700 font-medium mb-2">
          Integration Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          className="w-full border border-gray-300 rounded-md p-2"
          placeholder="My Notion Integration"
          value={formData.name || ''}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            name: e.target.value
          }))}
          required
        />
      </div>

      {!formData.access_token ? (
        <div>
          <button
            type="button"
            onClick={getNotionAuthUrl}
            disabled={authStatus === 'loading'}
            className="w-full py-3 bg-black text-white rounded-md hover:bg-gray-800 transition flex items-center justify-center"
          >
            {authStatus === 'loading' ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Authenticating...
              </>
            ) : (
              <>Authenticate With Notion</>
            )}
          </button>

          {authStatus === 'error' && (
            <p className="text-sm text-red-600 mt-2">
              Authentication failed. Please try again.
            </p>
          )}

          <p className="text-sm text-gray-600 mt-3">
            * Authenticate with Notion and give Hyperrank access to all Databases you want to use for publishing your articles.
          </p>
        </div>
      ) : (
        <>
          <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-start">
            <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-0.5" size={18} />
            <div>
              <p className="text-green-800 font-medium">Successfully connected to Notion</p>
              {formData.workspace_name && (
                <p className="text-sm text-green-700 mt-1">
                  Connected to workspace: {formData.workspace_name}
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Select Database <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                className="w-full border border-gray-300 rounded-md p-2 appearance-none bg-white pr-8"
                value={formData.database_id || ''}
                onChange={handleDatabaseChange}
                disabled={!databases.length || isLoadingDatabases}
                required
              >
                <option value="">Choose Notion Database</option>
                {databases.map(db => (
                  <option key={db.id} value={db.id}>{db.title}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {isLoadingDatabases && (
                <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Select the Notion database where you want to publish your content
            </p>
          </div>
        </>
      )}
    </form>
  );
};

export default NotionForm;

